import { setAuthProvider } from '@/shared/api'
import {
  getCurrentLinkToken,
  getCurrentLinkUuid,
  stripCurrentLinkTokenFromUrl,
} from '@/shared/lib/route-search-params'
import { setClientUidProvider } from '@/shared/lib/uid'
import { notifyNoticeChange } from '@/shared/lib/notice/notice-provider'
import { setStatelessNoticeHandler, setYjsTokenFetcher } from '@/shared/config/yjs-config'
import { requestYjsAuthToken } from '@/entities/flow/api/flow-api'
import {
  authStore,
  bootstrapAuthFromLinkToken,
  bootstrapAuthSession,
  hydrateAuthFromStorage,
  registerSharedLogoutSync,
  runGlobalLogout,
} from '@/features/auth'
import { projectStore } from '@/entities/project'
// 启动时把 auth store 适配为 AuthProvider 注入 http client。
// 这样 shared/api 不直接依赖 features/auth，符合 FSD 导入方向。
let lastCanvasVersion: number | undefined;

export function bootstrapAuth() {
  // 注入 yjs 通知分发能力
  setStatelessNoticeHandler(notifyNoticeChange)

  // 注入 yjs Token 鉴权获取逻辑
  setYjsTokenFetcher(async (projectId: string) => {
    try {
      const res = await requestYjsAuthToken(projectId, projectStore.shareId, projectStore.communityId, lastCanvasVersion);
      
      if (res.data?.canvas_version?.version !== undefined) {
        lastCanvasVersion = res.data.canvas_version.version;
      }

      if (res.data?.canvas_version?.need_update === 1 && res.data.canvas_version.update_info) {
        notifyNoticeChange({ 
          type: 'message', 
          uids: [], 
          info: { title: '版本更新', message: res.data.canvas_version.update_info } 
        });
      }

      if (res.data?.updateInfo) {
        notifyNoticeChange({ type: 'message', uids: [], info: res.data?.updateInfo });
      }
      if (res.code === 1 && res.data?.token) {
        return res.data.token;
      }
      return '';
    } catch (_error) {
      return '';
    }
  });

  setAuthProvider({
    getToken: () => authStore.loginData?.user_info?.token ?? '',
    getUuid: () => authStore.loginData?.user_info?.uuid ?? '',
    getTeamId: () => authStore.loginData?.current_team?.team_id || '',
    onAuthExpired: () => {
      const isInsideCanvas = window.location.hash.startsWith('#/flow')
      if (isInsideCanvas) {
        // 触发全局通知，让画布页面能够弹出“被踢下线”的弹窗
        const currentUid = authStore.loginData?.user_info?.uuid ?? ''
        if (currentUid) {
          notifyNoticeChange({
            type: 'tick',
            uids: [currentUid],
          })
        } else {
          runGlobalLogout()
        }
      } else {
        runGlobalLogout()
      }
    },
  })

  // 将 uid 来源指向 authStore，entities/flow 通过 getClientUid() 间接读取
  setClientUidProvider(() => authStore.loginData?.user_info?.uuid ?? '')
  registerSharedLogoutSync()

  const linkToken = getCurrentLinkToken()
  const linkUuid = getCurrentLinkUuid()
  if (linkToken && linkUuid) {
    stripCurrentLinkTokenFromUrl()
    void bootstrapAuthFromLinkToken(linkToken, linkUuid)
    return
  }

  // 1. 从 localStorage 同步恢复会话，让首屏路由守卫能立即拿到 initialized=true
  hydrateAuthFromStorage()

  // 2. 异步刷新已恢复的正式登录态（按 V1 设计，避免长期依赖旧缓存）
  //    刷新失败由 auth-actions 内部决定是清理还是保留兜底。
  void bootstrapAuthSession()
}
